import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { cloneElement, ReactElement, useEffect, useRef } from 'react';

export type FormikFieldWrapperProps = {
  /**
   * The field represents a string value.
   */
  field: string;

  /**
   * Represents the errors object for a Formik form.
   * This variable is of type FormikErrors<T>
   */
  errors?: FormikErrors<{ [key: string]: string }>;

  /**
   * Represents the touched state of form fields in a Formik form.
   */
  touched?: FormikTouched<{ [key: string]: boolean }>;

  /**
   * Represents the children of a React component.
   */
  children: ReactElement;
};

export function FormikFieldWrapper({
  children,
  field,
  errors,
  touched,
}: FormikFieldWrapperProps) {
  const formikContext = useFormikContext<{ [key: typeof field]: unknown }>();
  const errorFields = errors ?? formikContext.errors;
  const touchedFields = touched ?? formikContext.touched;
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (
      errorFields[field] === undefined
      || Object.keys(errorFields)[0] !== field
      || !ref.current
    ) {
      return;
    }
    ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }, [formikContext.submitCount]);

  return cloneElement(children, {
    ...children.props,
    ref: ref,
    error: children.props.error ?? (touchedFields[field] && !!errorFields[field]),
    helper: children.props.helper ?? (touchedFields[field] && errorFields[field]),
    'aria-invalid': children.props['aria-invalid'] ?? !!errorFields[field],
  });
}
